<template>
  <card>
		<template v-if="!hasGenCircuit">
			<template slot="header">
				<h5 v-if="$slots.title || title" class="card-title">
					<slot name="title">
						{{title}}
					</slot>
				</h5>
			</template>
			<div>
				<div class="loading-spinner" v-if="isLoading">
					<vue-loaders-ball-spin-fade-loader color="white" scale="1.2"/>
				</div>
				<div class="ct-chart" v-else>
					<v-chart class="chart" :option="option"/>
					<div class="total-value">
						<span>Equivalent to driving a standard petrol car</span>
					</div>
					<div class="car">
						<img src="@/assets/img/greencar.svg"/>
					</div>
				</div>
			</div>
		</template>
		<template v-else>
			<template slot="header">
				<h5 v-if="$slots.title || title" class="card-title">
					<slot name="title">
						{{title}}
					</slot>
				</h5>
			</template>
			<div>
				<div class="loading-spinner" v-if="isLoading">
					<vue-loaders-ball-spin-fade-loader color="white" scale="1.2"/>
				</div>
				<div style="height:304px;" v-else>
					<div class="solar-image">
						<img src="@/assets/img/solar-panel.png"/>
					</div>
					<div class="solar-value">
						<div style="margin: 0 auto;">
							<p> KWh:  {{ power }}</p>
							<p>  £: {{ cost }}</p>
						</div>
					</div>
				</div>
			</div>
		</template>
  </card>
</template>
<script>
import Card from "./Card.vue";
import { mapActions, mapGetters } from "vuex";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { GaugeChart } from "echarts/charts";
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent
} from "echarts/components";
import VChart  from "vue-echarts";
import QuerySetting from '@/mixins/querySetting';

use([
  CanvasRenderer,
	GaugeChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent
]);

export default {
  name: "carbon-footprint-card",
  mixins: [QuerySetting],
  components: {
    Card,
    VChart
  },
  props: {
    title: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      isLoading: false,
      totalCost: 0,
			power: 0,
			cost: 0,
			hasGenCircuit: false,
      option: {
				tooltip: {
					trigger: 'axis',
					axisPointer: {
						type: 'shadow'
					},
					backgroundColor: "#484E69",
					borderColor: "#484E69",
					textStyle: {
						color: 'white'
					},
					formatter: '{c}'
				},
				series: [
					{
						type: 'gauge',
						startAngle: 90,
						endAngle: -270,
						radius: "100%",
						min: 0 ,
						max: 100 ,
						color: ['#58E2C2', '#F7E53B'],
						pointer: {
							show: false
						},
						progress: {
							show: true,
							overlap: false,
							roundCap: true,
							width: 8,
							clip: false,
						},
						axisLine: {
							lineStyle: {
								width: 15,
								borderColor: 'white',
								borderWidth: 1,
								opacity: 0.2
							}
						},
						splitLine: {
							show: false,
							distance: 10,
							length: 10
						},
						axisTick: {
							show: false
						},
						axisLabel: {
							show: false,
							distance: 50
						},
						data: [
							{
								value: 20,
								name: 'Miles',
								title: {
									color: '#58E2C2',
									fontSize: 16,
									offsetCenter: ['0%', '-12%']
								},
								detail: {
									valueAnimation: true,
									color: 'white',
									fontSize: 16,
									offsetCenter: ['0%', '6%']
								}
							},
							{
								value: 40,
								name: '',
								title: {
									color: '#F7E53B',
									fontSize: 16,
									offsetCenter: ['0%', '30%']
								},
								detail: {
									valueAnimation: true,
									color: 'white',
									fontSize: 16,
									offsetCenter: ['0%', '46%'],
									formatter: '{value}kg'
								}
							},
							// {
							// 	value: 60,
							// 	name: 'CO2 This week',
							// 	title: {
							// 		color: '#2FDEAF',
							// 		fontSize: 14,
							// 		offsetCenter: ['0%', '38%']
							// 	},
							// 	detail: {
							// 		valueAnimation: true,
							// 		color: '#2FDEAF',
							// 		fontSize: 20,
							// 		offsetCenter: ['0%', '58%'],
							// 		formatter: '{value}kg'
							// 	}
							// }
						],
						center: ['50%', '50%']
					}
				]
      },
    };
  },
  computed: {
    ...mapGetters({
      periodTime: 'settime/getPeriodTime',
			currentDayPower			: 'influx/getCurrentDayPower',
			currentNightPower		: 'influx/getCurrentNightPower',
			defaultSiteId: 'auth/getDefaultSiteId',
			genPower: 'influx/getGenPower',
			user: 'auth/getUser'
    })
  },
  watch: {
    periodTime(newValue) {
      this.getCostData(newValue);
    },
    async defaultSiteId(newValue) {
			this.getCostData(this.periodTime);
		}
  },
  methods: {
    ...mapActions({
			getDisplayData: 'influx/getDisplayData',
      getDisplayGenCircuitData: 'influx/getDisplayGenCircuitData'
    }),
    async getCostData(value) {
      this.isLoading = true;
      let total = 0;
      let totalCO2 = 0;
			let miles = 0;
      await this.getDisplayData('overall');
      const dayPower = this.currentDayPower;
      const nightPower = this.currentNightPower;
			let currentPower = [];

			if (this.periodTime == 'today') {
				currentPower = nightPower.concat(dayPower);
			} else {
				dayPower.forEach((item, index) => {
					currentPower.push([
						item[0],
						!!nightPower[index]? item[1] + nightPower[index][1]: item[1]
					]);
				});
			}

			currentPower.forEach(d => total += d[1]);

			totalCO2 = total * 0.23314;
      miles = (totalCO2 / 0.19).toFixed(2);
      switch (this.periodTime) {
      	case 'today':
      		this.option.series[0].data[1].name = 'CO2 Today';
					this.option.series[0].max = 500;
      		break;
				case 'month':
					this.option.series[0].data[1].name = 'CO2 This Month';
					this.option.series[0].max = 10000;
					break;
				case 'year':
					this.option.series[0].data[1].name = 'CO2 This Year';
					this.option.series[0].max = 10000;
					break;
			}
      this.option.series[0].data[0].value = parseFloat(miles);
			this.option.series[0].data[1].value = parseFloat(parseFloat(totalCO2.toFixed(2)));

			if (this.hasGenCircuit) {
				await this.getDisplayGenCircuitData();
				let totalPower = 0;
				this.genPower.forEach(item => {
					totalPower += parseFloat(item[1]);
				});
				this.power = totalPower.toFixed(2);
				this.cost = (this.power * parseFloat(this.user.rate.cost_3)).toFixed(2);
			}
      this.isLoading = false;
    }
  },
  mounted() {
		this.user.devices.forEach(item => {
      if (item.device_type == 'Electricity Gen (Wh)') this.hasGenCircuit = true;
    });
		this.getCostData(this.periodTime);

		this.$nextTick(function () {
			window.setInterval(() => {
				this.getCostData(this.periodTime);
			},120000);
		});
  }
};
</script>
<style lang="scss" scoped>
.chart {
  height: 250px;
}
.total-value {
  position: absolute;
  top: -12%;
  left: 4%;
  text-align: center;

  span {
    font-size: 16px;
    font-family: 'Nunito Sans Regular';
    font-weight: 200;
    line-height: 1.1;
  }
}
.car {
	position: absolute;
	top: 24%;
	left: 41%;
}

.solar-image {
	text-align: center;
	padding: 20px 0;
}

.solar-value {
	text-align: center;
	color: yellow;
	display: flex;

	p {
		font-size: 18px;
		font-weight: 300;
		text-align: left;
	}
}
</style>
